import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import * as api from './api';
import type { AdoptionListingPayload } from './types';

const MINE = ['adoption', 'my-listings'] as const;
const ADOPTIONS = ['adoption', 'my-adoptions'] as const;

// ---- My listings ----
export function use_my_listings() {
  return useQuery({ queryKey: MINE, queryFn: api.fetch_my_listings });
}
export function use_my_listing(id: string | undefined) {
  return useQuery({ queryKey: ['adoption', 'listing', id], queryFn: () => api.fetch_my_listing(id as string), enabled: !!id });
}

export function use_listing_mutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: MINE });
  return {
    create: useMutation({ mutationFn: (input: AdoptionListingPayload) => api.create_listing(input), onSuccess: invalidate }),
    update: useMutation({
      mutationFn: ({ id, input }: { id: string; input: Partial<AdoptionListingPayload> }) => api.update_listing(id, input),
      onSuccess: (l) => {
        invalidate();
        qc.invalidateQueries({ queryKey: ['adoption', 'listing', l.id] });
      },
    }),
    remove: useMutation({ mutationFn: (id: string) => api.delete_listing(id), onSuccess: invalidate }),
    mark_adopted: useMutation({ mutationFn: (id: string) => api.mark_adopted(id), onSuccess: invalidate }),
    close: useMutation({ mutationFn: (id: string) => api.close_listing(id), onSuccess: invalidate }),
  };
}

// ---- Photos ----
export function use_listing_photos(id: string | undefined) {
  return useQuery({ queryKey: ['adoption', 'photos', id], queryFn: () => api.fetch_listing_photos(id as string), enabled: !!id });
}
export function use_listing_photo_mutations(id: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['adoption', 'photos', id] });
  return {
    upload: useMutation({ mutationFn: (file: File) => api.upload_listing_photo(id, file), onSuccess: invalidate }),
    remove: useMutation({ mutationFn: (photo_id: string) => api.delete_listing_photo(id, photo_id), onSuccess: invalidate }),
  };
}

// ---- OTP publish ----
export function use_send_listing_otp(id: string) {
  return useMutation({ mutationFn: () => api.send_listing_otp(id) });
}
export function use_verify_listing_otp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.verify_listing_otp(id, code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MINE });
      qc.invalidateQueries({ queryKey: ['adoption', 'listing', id] });
    },
  });
}

// ---- Interested adopters ----
export function use_listing_requests(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['adoption', 'listing-requests', id],
    queryFn: () => api.fetch_listing_requests(id as string),
    enabled: !!id && enabled,
  });
}

// ---- Browse ----
export function use_browse(filters: api.BrowseFilters) {
  return useQuery({
    queryKey: ['adoption', 'browse', filters],
    queryFn: () => api.browse_listings(filters),
    placeholderData: keepPreviousData,
    staleTime: 20_000,
  });
}
export function use_browse_detail(id: string | undefined) {
  return useQuery({ queryKey: ['adoption', 'browse', 'detail', id], queryFn: () => api.fetch_browse_detail(id as string), enabled: !!id });
}

// ---- Adopter requests ----
export function use_create_request() {
  return useMutation({ mutationFn: (listing_id: string) => api.create_request(listing_id) });
}
export function use_pay_request() {
  return useMutation({ mutationFn: (request_id: string) => api.pay_request(request_id) });
}
export function use_verify_request_payment() {
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string } }) =>
      api.verify_request_payment(id, input),
  });
}
export function use_my_adoptions() {
  return useQuery({ queryKey: ADOPTIONS, queryFn: api.fetch_my_adoptions });
}
